import type { CSSProperties, FC } from 'react';
import { useMemo, useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';

import { Toast } from 'antd-mobile';
import useQuery from '@/hooks/useQuery';
import type {
  MenuListProductItem,
  PromotionCouponsCardDiscountItem,
  MenuBannerItem,
  PromotionCouponProductsType,
} from '@/apis/product/types';
import type { PutCartResponseDataCouponItemType } from '@/apis/product';
import { useCartAction, useCartState } from '@/hooks/useCart';
import useAppTitle from '@/hooks/useTitle';
import { useLoginState } from '@/hooks/useLoginState';
import { MY_ADDRESS, SELECT_CANTEEN, COUPONS_DETAIL, HOME, AVAILABLE_CARD_LIST } from '@/constants/path';
import { EAPP_CHANNEL_VALUE, getAppChannelValue, isDouyinAppChannel } from '@/channels';

import ProductBanner from './Banner';
import {
  ProductMenuList,
  ProductMenuStoreInfo,
  ProductMenuCouponDiscount,
  FakerProductMenuCouponDiscount,
  ProductMenuListSkeleton,
  ProductMenuStoreInfoSkeleton,
  useStoreDaypartTimer,
} from './Menu';
import ProductFakerSearch from './Search/Faker';
import { EMenusPageErrEvent } from './Error/types';
import ProductError from './Error';
import CouponLimitList from './Coupon/Limit/List';

import useProductData from './useData';
import { useScrollLayout, BANNER_HEIGHT, SEARCH_NODE_HEIGHT } from './useScrollLayout';
import { useLocationAutoCoupon, useGetCouponProductList } from './useCoupon';
import { useGoToAddressBefore, useScrollState } from './external';
import { trackAddToCartByListEV, trackAddToCartByCouponEV } from './external/trackinfo';
import Spin from './external/Spin';
import showCouponModal from './external/CouponModal/show';

import './index.less';
import { trackEvent } from '@/utils/tracker';
import { channelCallPhone } from '@/channels/phone';

const ProductCartModule = lazy(() => import('./Cart'));
const DaypartDatePopup = lazy(() => import('./Menu/Store/DatePopup'));

type UrlQuery = {
  orderType?: string;
  // for orderType 1
  storeCode?: string;
  // TDDO unknown
  newStoreCode?: string;

  // for orderType 2
  addressId?: string;
  // TDDO unknown
  newAddressId?: string;

  /** 再来一单 订单ID */
  orderId?: string;
  /** 是否可以更改地址/餐厅 1 or 0 @default '1' */
  changeAddress?: string;

  /** 推荐的产品code, 将自动滚动到此处 */
  recommendProductCode?: string;
  /** 推荐产品分类code, 将自动滚动到此处 */
  recommendCategoryCode?: string;
};

export type ProductPageProps = {
  orderType?: number;
  style?: CSSProperties & { '--pp-bottom-gap'?: CSSProperties['bottom'] };
};

const defaultProps: Partial<ProductPageProps> = {
  orderType: 1,
};

const ProductPage: FC<ProductPageProps> = (props) => {
  const { orderType: propsOrderType, style } = props;
  const location = useLocation();
  const query = useQuery<UrlQuery>();
  const orderType = Number(query?.orderType) || propsOrderType;
  const changeAddress = Boolean(query?.changeAddress != null ? query.changeAddress === '1' : true);

  const { loggedIn } = useLoginState();

  useAppTitle({
    navBackgroundColor: '00FFFFFF',
    appletStyle: 'black',
    backBtnVisible: '1',
    appletTitleBarVisible: '1',
  });

  const [scrollState, scrollStateAction] = useScrollState();

  const [cartstate, , errCartResponse] = useCartState(orderType);
  const cartCouponCodes = useMemo(
    () => cartstate?.value?.products?.map((p) => p.coupon?.code)?.filter(Boolean) as string[],
    [cartstate?.value?.products],
  );

  const navigate = useNavigate();

  const [datePopupVisible, setDatePopupVisible] = useState(false);

  const {
    errorStatus,
    vicinityStatusCode,
    activeStore,
    baseLoading,
    storeLoading,
    addressInfo,
    tempLocation,
    storeInfo,
    refreshStoreOrAddressInfo,
    daypartCode,
    daypart,
    menuList,
    menuListLoaing,
    refreshMenuList,
    menuBanner,
    queryAppointmentStr,
    curDateInfo,
    updateDeliveryDateInfo,
    menuRightCardInfo,
  } = useProductData({
    orderType,
    storeCode: query?.newStoreCode || query?.storeCode,
    addressId: query?.newAddressId || query?.addressId,
  });

  useStoreDaypartTimer({
    dayparts: storeInfo?.dayparts,
    curDaypart: (!curDateInfo?.time || !curDateInfo?.date) && activeStore ? daypartCode : undefined,
    endCallback: () => {
      void refreshStoreOrAddressInfo();
    },
  });

  const { updateCart, clearErrCartResponse } = useCartAction();

  const appChannel = useMemo(() => getAppChannelValue(), []);
  // 抖音渠道不展示 banner
  const allwaysFullScreen = useMemo(() => appChannel === EAPP_CHANNEL_VALUE.DOUYIN, [appChannel]);
  const { containerRef, listPanelRef, fullScreen, menuListRef, storeNodeRef, menuListOffsetTop } = useScrollLayout({
    storeEffectDeps: [!baseLoading, !!storeInfo?.name],
    allwaysFullScreen,
  });

  useEffect(() => {
    if (scrollState?.scrollY && containerRef.current) {
      containerRef.current?.scroll({ top: scrollState.scrollY });

      if (baseLoading === false && !errorStatus && activeStore) {
        setTimeout(() => {
          containerRef.current?.scroll({ top: scrollState.scrollY });
          scrollStateAction.set({ scrollY: undefined });
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore, baseLoading, errorStatus, scrollState?.scrollY]);

  const onSearchClick = useCallback(() => {
    // cache scrollY
    scrollStateAction.set({ scrollY: containerRef.current?.scrollTop });
    trackEvent('searchButtonClick', {
      belong_page: orderType === 1 ? '到店取餐菜单页' : '麦乐送菜单页',
    });
    navigate({
      pathname: '/product/search',
      search: new URLSearchParams({
        storeCode: storeInfo?.code || '',
        daypartCode: String(daypartCode),
        orderType: String(orderType),
        beCode: storeInfo?.beCode || '',
        addressId: addressInfo?.id || '',
        appointment: queryAppointmentStr,
      }).toString(),
    });
  }, [
    addressInfo?.id,
    containerRef,
    daypartCode,
    navigate,
    orderType,
    queryAppointmentStr,
    scrollStateAction,
    storeInfo?.beCode,
    storeInfo?.code,
  ]);

  const onProductCardClick = useCallback(
    (p: MenuListProductItem) => {
      if (p.sellout) {
        Toast.show({
          content: '餐品已售罄',
          position: 'center',
        });
        return;
      }
      // cache scrollY
      scrollStateAction.set({ scrollY: containerRef.current?.scrollTop });

      navigate({
        pathname: `/product/${p.productCode}`,
        search: new URLSearchParams({
          storeCode: storeInfo?.code || '',
          daypartCode: String(daypartCode),
          orderType: String(orderType),
          beCode: storeInfo?.beCode || '',
          appointment: queryAppointmentStr,
          productPromotions: !isEmpty(p.productPromotions) ? JSON.stringify(p.productPromotions) : '',
        }).toString(),
      });
    },
    [
      containerRef,
      daypartCode,
      navigate,
      orderType,
      queryAppointmentStr,
      scrollStateAction,
      storeInfo?.beCode,
      storeInfo?.code,
    ],
  );

  const onProductAddAction = useCallback(
    async (item: MenuListProductItem) => {
      try {
        trackAddToCartByListEV(item, daypartCode, orderType);

        await updateCart({
          cartType: '1',
          orderType: Number(orderType),
          daypartCode: String(daypartCode),
          storeCode: storeInfo?.code || '',
          beCode: storeInfo?.beCode || '',
          date: curDateInfo?.date,
          time: curDateInfo?.time,
          products: [
            {
              quantity: 1,
              code: item.productCode,
              name: item.productName,
              type: item.productType,
            },
          ],
        });
      } catch (error) {
        const errMsg = (error as Error)?.message || '加入购物车失败';
        Toast.show({
          content: errMsg,
          position: 'center',
        });
      }
    },
    [curDateInfo?.date, curDateInfo?.time, daypartCode, orderType, storeInfo?.beCode, storeInfo?.code, updateCart],
  );
  const onProductSubAction = useCallback(
    async (item: MenuListProductItem) => {
      const curCartProduct = cartstate?.value?.products?.find((p) => p.code === item.productCode);

      if (curCartProduct) {
        try {
          await updateCart({
            cartType: '1',
            orderType: Number(orderType),
            daypartCode: String(daypartCode),
            storeCode: storeInfo?.code || '',
            beCode: storeInfo?.beCode || '',
            date: curDateInfo?.date,
            time: curDateInfo?.time,
            products: [
              {
                sequence: curCartProduct.sequence,
                quantity: -1,
                code: item.productCode,
                name: item.productName,
                type: item.productType,
              },
            ],
          });
        } catch (error) {
          const errMsg = (error as Error)?.message || '移除失败';
          Toast.show({
            content: errMsg,
            position: 'center',
          });
        }
      }
    },
    [
      cartstate?.value?.products,
      curDateInfo?.date,
      curDateInfo?.time,
      daypartCode,
      orderType,
      storeInfo?.beCode,
      storeInfo?.code,
      updateCart,
    ],
  );

  const [toAddressBeforeCache] = useGoToAddressBefore(orderType);
  const onChangeAddress = useCallback(() => {
    if (orderType === 1) {
      // storeAddress page start ---
      toAddressBeforeCache();
      // --- end storeAddress page
      navigate({
        pathname: SELECT_CANTEEN,
      });
    } else if (orderType === 2) {
      // storeAddress page start ---
      toAddressBeforeCache();
      // --- end storeAddress page
      navigate({
        pathname: MY_ADDRESS,
      });
    }
  }, [navigate, orderType, toAddressBeforeCache]);

  const onErrButtonClick = useCallback(
    (type?: EMenusPageErrEvent) => {
      if (type === EMenusPageErrEvent.APPOINTMENT) {
        setDatePopupVisible(true);
      } else if (type === EMenusPageErrEvent.RELOAD_MENUS) {
        refreshMenuList();
      } else {
        onChangeAddress();
      }
    },
    [onChangeAddress, refreshMenuList],
  );

  // 优惠券冲突 ----------
  const showCouponModalSelector = useCallback(
    (text: string, coupons?: PutCartResponseDataCouponItemType[], useCoupon?: PutCartResponseDataCouponItemType) => {
      const _coupons = useCoupon ? [useCoupon, ...(coupons || [])] : coupons;

      const { close: closeCouponModal } = showCouponModal({
        content: (
          <div>
            <div style={{ margin: '10px 0' }}>{text}</div>
            <CouponLimitList
              items={_coupons}
              onRemove={async (couponItem) => {
                const _type = couponItem?.button?.type;
                // [TODO] 暂不支持 2,3 type. 2 为当前需求场景临时处理
                if (_type && [2, 4].includes(_type)) {
                  const _selectedCoupon = useCoupon
                    ? useCoupon
                    : coupons?.filter(
                        (c) => c.couponId !== couponItem.couponId && c.couponCode !== couponItem.couponCode,
                      )?.[0];

                  const _removeProducts =
                    coupons && useCoupon && _type === 2
                      ? coupons.map((c) => ({
                          quantity: -1,
                          cardId: c.cardId,
                          cardType: c.cardType,
                          couponCode: c.couponCode,
                          couponId: c.couponId,
                          promotionId: c.promotionId,
                          membershipCode: c.membershipCode,
                        }))
                      : [
                          {
                            quantity: -1,
                            cardId: couponItem.cardId,
                            cardType: couponItem.cardType,
                            couponCode: couponItem.couponCode,
                            couponId: couponItem.couponId,
                            promotionId: couponItem.promotionId,
                            membershipCode: couponItem.membershipCode,
                          },
                        ];

                  if (_selectedCoupon) {
                    await updateCart({
                      cartType: '1',
                      orderType: Number(orderType),
                      daypartCode: String(daypartCode),
                      storeCode: storeInfo?.code || '',
                      beCode: storeInfo?.beCode || '',
                      date: curDateInfo?.date,
                      time: curDateInfo?.time,
                      operation: 0,
                      products: [
                        {
                          quantity: 1,
                          operationType: 0,
                          couponCode: _selectedCoupon.couponCode,
                          couponId: _selectedCoupon.couponId,
                          promotionId: _selectedCoupon.promotionId,
                        },
                        ..._removeProducts,
                      ],
                    });
                  }
                }

                closeCouponModal();
              }}
            />
          </div>
        ),
        grayBg: true,
        closeOnMaskClick: true,
        showCloseButton: true,
        afterClose: () => {
          clearErrCartResponse();
        },
      });
    },
    [
      clearErrCartResponse,
      curDateInfo?.date,
      curDateInfo?.time,
      daypartCode,
      orderType,
      storeInfo?.beCode,
      storeInfo?.code,
      updateCart,
    ],
  );
  useEffect(() => {
    // [TODO] 暂时只开放抖音渠道
    if (isDouyinAppChannel()) {
      if (errCartResponse?.pmtLimit?.text) {
        showCouponModalSelector(errCartResponse.pmtLimit.text, errCartResponse.pmtLimit.coupons);
      } else if (errCartResponse?.conflictCoupons?.text) {
        showCouponModalSelector(
          errCartResponse.conflictCoupons.text,
          errCartResponse.conflictCoupons.coupons,
          errCartResponse.conflictCoupons.useCoupon,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errCartResponse]);

  const { createCouponProductListReq } = useGetCouponProductList();
  const addCouponDiscountToCart = useCallback(
    async (
      multipleProducts?: boolean,
      obj?: Pick<PromotionCouponsCardDiscountItem, 'id' | 'code' | 'promotionId' | 'title'>,
      couponProducts?: PromotionCouponProductsType,
    ) => {
      if (obj?.code && obj?.id) {
        if (multipleProducts) {
          navigate({
            pathname: '/product/coupon/select',
            search: new URLSearchParams({
              storeCode: storeInfo?.code || '',
              daypartCode: String(daypartCode),
              orderType: String(orderType),
              beCode: storeInfo?.beCode || '',
              couponCode: obj.code || '',
              couponId: obj.id || '',
              promotionId: obj.promotionId || '',
              name: obj.title || '',
              appointment: queryAppointmentStr,
            }).toString(),
          });
        } else {
          let _couponProducts = couponProducts;
          if (!_couponProducts && obj.code && obj.id) {
            try {
              const [_res] = await createCouponProductListReq({
                storeCode: storeInfo?.code || '',
                daypartCode: String(daypartCode),
                orderType: String(orderType),
                beCode: storeInfo?.beCode || '',
                date: curDateInfo?.date,
                time: curDateInfo?.time,
                couponCode: obj.code,
                couponId: obj.id,
                promotionId: obj.promotionId,
              }).ready();
              _couponProducts = _res;
            } catch (error) {
              console.error('[addCouponDiscountToCart]-craeteCouponProductListReq error.', error);
            }
          }

          const _p =
            _couponProducts?.productList && Array.isArray(_couponProducts.productList)
              ? _couponProducts.productList[0]
              : undefined;

          if (
            _p &&
            _p.supportChoice === 1 &&
            _p.productCode &&
            _p.productType &&
            [1, 2, 3, 7].includes(_p.productType)
          ) {
            navigate({
              pathname: `/product/${_p.productCode}`,
              search: new URLSearchParams({
                storeCode: storeInfo?.code || '',
                daypartCode: String(daypartCode),
                orderType: String(orderType),
                beCode: storeInfo?.beCode || '',
                couponCode: obj.code,
                couponName: obj.title || '',
                couponId: obj.id,
                promotionId: obj.promotionId || '',
                appointment: queryAppointmentStr,
                disabledCount: '1',
              }).toString(),
            });
            return;
          }
          try {
            trackAddToCartByCouponEV(
              _p,
              obj.title,
              daypartCode,
              orderType,
              orderType === 2 ? '麦乐送菜单页' : '到店取餐菜单页',
            );

            await updateCart({
              cartType: '1',
              orderType: Number(orderType),
              daypartCode: String(daypartCode),
              storeCode: storeInfo?.code || '',
              beCode: storeInfo?.beCode || '',
              date: curDateInfo?.date,
              time: curDateInfo?.time,
              operation: 0,
              products: [
                {
                  operationType: 0,
                  couponCode: obj.code,
                  couponId: obj.id,
                  promotionId: obj.promotionId,
                  quantity: 1,
                },
              ],
            });
          } catch (error) {
            const errMsg = (error as Error)?.message || '加入购物车失败';
            Toast.show({
              content: errMsg,
              position: 'center',
            });
          }
        }
      }
    },
    [
      createCouponProductListReq,
      curDateInfo?.date,
      curDateInfo?.time,
      daypartCode,
      navigate,
      orderType,
      queryAppointmentStr,
      storeInfo?.beCode,
      storeInfo?.code,
      updateCart,
    ],
  );
  const onCouponDiscountAdd = useCallback(
    (item: PromotionCouponsCardDiscountItem) => {
      void addCouponDiscountToCart(item.multipleProducts, item);
    },
    [addCouponDiscountToCart],
  );
  // 购物券 路由参数检查
  const checkAutoAddCoupon = useLocationAutoCoupon({
    params: {
      storeCode: storeInfo?.code || '',
      daypartCode: String(daypartCode),
      orderType: String(orderType),
      beCode: storeInfo?.beCode || '',
      date: curDateInfo?.date,
      time: curDateInfo?.time,
    },
    callback: (item, state) =>
      addCouponDiscountToCart(
        item?.multipleProducts,
        {
          code: state.couponCode,
          id: state.couponId,
          promotionId: state.promotionId,
          title: item?.productList?.[0]?.productName,
        },
        item,
      ),
    onError: (msg) => {
      Toast.show({
        content: msg,
        position: 'center',
      });
    },
  });
  useEffect(() => {
    if (activeStore && baseLoading === false) {
      void checkAutoAddCoupon();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore, baseLoading]);

  const onCouponDiscountCardClick = useCallback(
    (item: PromotionCouponsCardDiscountItem) => {
      if (item.code && item.id) {
        trackEvent('couponClick', {
          belong_page: orderType === 1 ? '到店取餐菜单页' : '麦乐送菜单页',
          card_id: item.id,
          coupon_name: item.title,
          coupon_type: orderType === 1 ? '到店专用' : '外送专用',
        });

        navigate({
          pathname: COUPONS_DETAIL,
          search: new URLSearchParams({
            code: item.code,
            id: item.id,
          }).toString(),
        });
      }
    },
    [navigate, orderType],
  );
  const onCouponDiscountShowMore = useCallback(() => {
    navigate({
      pathname: AVAILABLE_CARD_LIST,
      search: new URLSearchParams({
        storeCode: storeInfo?.code || '',
        daypartCode: String(daypartCode),
        orderType: String(orderType),
        beCode: storeInfo?.beCode || '',
        beType: storeInfo?.beType || '',
        date: curDateInfo?.date || '',
        time: curDateInfo?.time || '',
        scene: '2',
      }).toString(),
    });
  }, [
    curDateInfo?.date,
    curDateInfo?.time,
    daypartCode,
    navigate,
    orderType,
    storeInfo?.beCode,
    storeInfo?.beType,
    storeInfo?.code,
  ]);

  const onBannerItemClick = useCallback(
    (item: MenuBannerItem) => {
      if (item?.redirectUrl) {
        // 是否为当前页面
        const _isCurrentPath = /^\/product\?((=?&?[a-zA-Z0-9_-](\?)?)*)*$/i.test(item.redirectUrl);
        const _redirectUrl = _isCurrentPath ? `${item.redirectUrl}&withcache=1` : item.redirectUrl;
        navigate(_redirectUrl, { replace: _isCurrentPath });
      }
    },
    [navigate],
  );

  const onCartsNavToProductDetailCallback = useCallback(() => {
    // cache scrollY
    scrollStateAction.set({ scrollY: containerRef.current?.scrollTop });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollStateAction]);

  const onAutoscrollCompleteByUrl = useCallback(() => {
    const { recommendProductCode, recommendCategoryCode, ...restQuery } = query || {};
    navigate(
      {
        pathname: location.pathname,
        search: new URLSearchParams(restQuery as Record<string, string>).toString(),
      },
      { replace: true },
    );
  }, [location.pathname, navigate, query]);

  useEffect(() => {
    if (menuRightCardInfo?.menuDiscount?.discountItems) {
      menuRightCardInfo.menuDiscount.discountItems
        .filter((i) => i.code !== 'CPN_KINGS_LIFE')
        .forEach((i) => {
          trackEvent('couponExposure', {
            belong_page: orderType === 1 ? '到店取餐菜单页' : '麦乐送菜单页',
            card_id: i.id,
            coupon_name: i.title,
            coupon_type: orderType === 1 ? '到店专用' : '外送专用',
          });
        });
    }
  }, [menuRightCardInfo?.menuDiscount, orderType]);

  // [PATCH] switch address/store history
  useEffect(() => {
    const _goHome = () => {
      navigate(HOME, { replace: true });
    };
    window.addEventListener('popstate', _goHome);
    return () => window.removeEventListener('popstate', _goHome);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // --- patch end

  return (
    <div className="product-container" ref={containerRef} style={style}>
      <div
        className="product-search-wrapper"
        style={{ '--height': SEARCH_NODE_HEIGHT } as CSSProperties}
        data-full={fullScreen}>
        <ProductFakerSearch onClick={onSearchClick} />
      </div>
      {!allwaysFullScreen ? (
        <ProductBanner
          className="product-banner"
          items={menuBanner}
          itemHeight={BANNER_HEIGHT}
          loop
          autoplay={baseLoading === false && !fullScreen}
          autoplayInterval={5000}
          onItemClick={onBannerItemClick}
        />
      ) : (
        <div style={{ height: SEARCH_NODE_HEIGHT }} />
      )}
      <div className="product-menu-list-wrapper" ref={listPanelRef} data-full={fullScreen}>
        {(!storeLoading && storeInfo) || !baseLoading ? (
          <ProductMenuStoreInfo
            ref={storeNodeRef}
            className="product-store-container"
            orderType={orderType}
            data={storeInfo}
            address={addressInfo?.address || tempLocation?.name}
            appointmentDate={{ date: curDateInfo?.date, time: curDateInfo?.time }}
            hideDaypartDisplay={!!errorStatus}
            showSwitchAddress={changeAddress}
            onChangeAddress={onChangeAddress}
            onSwitchTime={() => setDatePopupVisible(true)}
          />
        ) : (
          <ProductMenuStoreInfoSkeleton />
        )}
        {activeStore && baseLoading === false && appChannel === EAPP_CHANNEL_VALUE.DOUYIN && !loggedIn && (
          <FakerProductMenuCouponDiscount className="product-coupon-discount" />
        )}
        {activeStore && menuRightCardInfo?.menuDiscount && (
          <Spin loading={baseLoading}>
            <ProductMenuCouponDiscount
              className="product-coupon-discount"
              selectedCodes={cartCouponCodes}
              data={menuRightCardInfo.menuDiscount}
              onAdd={onCouponDiscountAdd}
              onCardClick={onCouponDiscountCardClick}
              onMore={onCouponDiscountShowMore}
            />
          </Spin>
        )}
        {(menuListLoaing || baseLoading) && isEmpty(menuList?.menu) ? (
          <ProductMenuListSkeleton className="product-menu-list" />
        ) : errorStatus ? (
          <ProductError
            status={errorStatus}
            subErrCode={vicinityStatusCode}
            orderType={orderType}
            onBtnClick={onErrButtonClick}
          />
        ) : activeStore ? (
          <Spin loading={menuListLoaing || baseLoading}>
            <ProductMenuList
              ref={menuListRef}
              wrapperScrollRef={containerRef}
              className="product-menu-list"
              orderType={orderType}
              storeInfo={storeInfo}
              daypart={daypart}
              items={menuList?.menu}
              cartQuantityGroup={cartstate?.productQuantityGroup}
              sideCanAutoScroll={fullScreen}
              offsetTop={menuListOffsetTop}
              autoScroll={
                menuListLoaing === false && menuListOffsetTop != null
                  ? {
                      productCode: query?.recommendProductCode,
                      categoryCode: query?.recommendCategoryCode,
                      onComplete: onAutoscrollCompleteByUrl,
                    }
                  : undefined
              }
              onCardClick={onProductCardClick}
              onCardSelect={onProductCardClick}
              onCardAdd={onProductAddAction}
              onCardSub={onProductSubAction}
            />
          </Spin>
        ) : null}
      </div>

      <Suspense>
        {activeStore && !errorStatus && storeInfo && !baseLoading && (
          <ProductCartModule
            cartType={1}
            addressId={addressInfo?.id}
            orderType={orderType}
            storeCode={storeInfo?.code}
            daypartCode={String(daypartCode)}
            other={{
              beCode: storeInfo.beCode || undefined,
              beType: storeInfo.beType || undefined,
            }}
            navToProductDetailCallback={onCartsNavToProductDetailCallback}
          />
        )}
        {!baseLoading && storeInfo && orderType === 2 && (
          <DaypartDatePopup
            params={{
              storeCode: storeInfo.code,
              dayPartCode: daypartCode || undefined,
              beCode: storeInfo.beCode || undefined,
              beType: storeInfo.beType || undefined,
            }}
            visible={datePopupVisible}
            value={[curDateInfo?.date, daypartCode || undefined, curDateInfo?.time]}
            onClose={() => setDatePopupVisible(false)}
            onConfirm={(_d, _daypartCode, _time) => {
              updateDeliveryDateInfo({
                date: _d,
                daypartCode: _daypartCode,
                time: _time,
              });
            }}
          />
        )}
      </Suspense>

      {isDouyinAppChannel() && (
        <div
          className="product-customer-service-btn"
          onClick={() => {
            trackEvent('customerServiceClick', {
              belong_page: orderType === 1 ? '到店取餐菜单页' : '麦乐送菜单页',
              button_name: '联系客服',
            });
            channelCallPhone('4009-200-205');
          }}>
          <svg className="icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              fill="white"
            />
          </svg>
          <span className="text">联系客服</span>
        </div>
      )}
    </div>
  );
};

ProductPage.defaultProps = defaultProps;

export default ProductPage;
